import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { FunctionComponent } from 'react';

interface ShortcutEntry {
  keys: string[];
  label: string;
}

interface ShortcutSection {
  title: string;
  shortcuts: ShortcutEntry[];
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const modLabel = isMac ? '⌘' : 'Ctrl';
const altLabel = isMac ? '⌥' : 'Alt';

const Kbd = ({ children }: { children: string }) => (
  <kbd className='inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-md border border-slate-200 bg-slate-50 text-xs font-mono font-medium text-slate-600 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-300'>
    {children}
  </kbd>
);

const ShortcutRow = ({ keys, label }: ShortcutEntry) => (
  <div className='flex items-center justify-between py-1.5'>
    <span className='text-sm text-slate-700 dark:text-stone-300'>{label}</span>
    <div className='flex items-center gap-1'>
      {keys.map((key, i) => (
        <Kbd key={i}>{key}</Kbd>
      ))}
    </div>
  </div>
);

interface iKeyboardShortcutsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const KeyboardShortcutsDialog: FunctionComponent<iKeyboardShortcutsDialogProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation('common');
  const { t: tProject } = useTranslation('project');

  const sections: ShortcutSection[] = [
    {
      title: t('shortcuts.sections.general'),
      shortcuts: [
        { keys: ['?'], label: t('shortcuts.showShortcuts') },
        { keys: [modLabel, 'B'], label: t('shortcuts.toggleSidebar') },
        { keys: [modLabel, 'Shift', 'H'], label: t('shortcuts.goToDashboard') },
        { keys: [modLabel, 'Shift', 'D'], label: t('shortcuts.toggleTheme') },
        { keys: ['Escape'], label: t('shortcuts.closeDialog') },
      ],
    },
    {
      title: t('shortcuts.sections.project'),
      shortcuts: [
        { keys: [modLabel, 'Shift', 'L'], label: t('shortcuts.toggleLayers') },
        { keys: [modLabel, 'Shift', 'U'], label: t('shortcuts.openUpload') },
        { keys: [modLabel, 'Shift', 'E'], label: t('shortcuts.editProject') },
        { keys: [modLabel, altLabel, 'A'], label: tProject('viewer.openAnalyses') },
        { keys: [modLabel, 'Shift', 'M'], label: t('shortcuts.toggleMeasureDistance') },
        { keys: [modLabel, 'Shift', 'A'], label: t('shortcuts.toggleMeasureArea') },
      ],
    },
    {
      title: t('shortcuts.sections.viewer'),
      shortcuts: [
        { keys: ['M'], label: t('shortcuts.toggleMap') },
        { keys: ['P'], label: t('shortcuts.togglePointcloud') },
        { keys: ['C'], label: t('shortcuts.toggleClipBox') },
        { keys: ['O'], label: t('shortcuts.toggleOrthoCamera') },
        { keys: ['T'], label: t('shortcuts.toggleThirdPerson') },
        { keys: ['G'], label: t('shortcuts.toggleStreetView') },
        { keys: ['R'], label: t('shortcuts.toggleMeasureTool') },
        { keys: ['B'], label: t('shortcuts.toggleAreaTool') },
        { keys: ['I'], label: t('shortcuts.takeScreenshot') },
        { keys: ['Shift', 'L'], label: t('shortcuts.cycleMapLineweight') },
      ],
    },
    {
      title: t('shortcuts.sections.flyingControls'),
      shortcuts: [
        { keys: ['W'], label: t('shortcuts.flyForward') },
        { keys: ['S'], label: t('shortcuts.flyBackward') },
        { keys: ['A'], label: t('shortcuts.flyLeft') },
        { keys: ['D'], label: t('shortcuts.flyRight') },
        { keys: ['Space'], label: t('shortcuts.flyUp') },
        { keys: ['Q'], label: t('shortcuts.flyDown') },
        { keys: ['Shift'], label: t('shortcuts.flyFaster') },
      ],
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[540px] max-h-[80vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{t('shortcuts.title')}</DialogTitle>
          <DialogDescription>{t('shortcuts.description')}</DialogDescription>
        </DialogHeader>
        <div className='space-y-6 mt-2'>
          {sections.map((section) => (
            <div key={section.title}>
              <h3 className='text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-stone-500 mb-2'>
                {section.title}
              </h3>
              <div className='divide-y divide-slate-100 dark:divide-stone-800'>
                {section.shortcuts.map((shortcut) => (
                  <ShortcutRow key={shortcut.label} {...shortcut} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default KeyboardShortcutsDialog;
